import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { useToasts } from "react-toast-notifications";
import { useMutation } from "../../../GraphQL/helpers";
import { Row, Column } from "../../global/grid";
import Button from "../../global/button";
import Modal, { ModalBody, ModalFooter } from "../../global/modal";
import Alert from "../../global/alert";
import Form, { FormInput, FormSelect, FormCheckbox } from "../../global/forms";
import Validator from "../../../utilities/validators";
import SpiMasked from "../../global/spiMasked";
import errorHandler from "../../../utilities/errorHandler";
import GET_PROFILE from "../../../GraphQL/queries/fetchProfile";
import { ADD_EMAIL, UPDATE_EMAIL } from "../../../GraphQL/mutations/email";

const EmailModal = ({
    email,
    isNewEmail,
    onClose,
    innerRef,
    ...attributes
}) => {
    const { t } = useTranslation(["components.profile.emailModal", "utilities.validators"]);
    const validatorsLocale = t("utilities.validators:validators", { returnObjects: true });
    const validator = new Validator(validatorsLocale);
    const emailAddressTypes = t("emailAddressType.options", { returnObjects: true });
    const { addToast } = useToasts();
    const formHook = useForm();
    const { handleSubmit, formState } = formHook;
    const { isSubmitting } = formState;
    const [addEmail, { loading: addLoading }] = useMutation(ADD_EMAIL);
    const [updateEmail, { loading: updateLoading }] = useMutation(UPDATE_EMAIL);
    const [modalError, setModalError] = useState(null);
    const [loading, setLoading] = useState(false);
    let modalRef = useRef(null);
    if (innerRef) {
        modalRef = innerRef;
    }

    useEffect(() => {
        setLoading(addLoading || updateLoading);
    }, [addLoading, updateLoading]);

    const onCloseHandler = () => {
        setModalError(null);
        onClose && onClose();
    };

    const onSubmit = async (data) => {
        let result;

        try {
            if (isNewEmail) {
                result = addEmail({
                    variables: {
                        emailAddress: data.emailAddress,
                        emailType: data.emailAddressType,
                    },
                    optimisticResponse: {
                        agregarEmail: {
                            email: {
                                id: 0,
                                direccion: data.emailAddress,
                                tipoEmail: data.emailAddressType,
                                principal: false,
                                confirmado: false,
                                __typename: "Email",
                            },
                            errores: null,
                        }
                    },
                    update(cache, { data: { agregarEmail } }) {
                        const { perfil } = cache.readQuery({ query: GET_PROFILE });

                        if (agregarEmail?.email) {
                            cache.writeQuery({
                                query: GET_PROFILE,
                                data: {
                                    perfil: {
                                        ...perfil,
                                        emails: [...perfil.emails, agregarEmail.email],
                                    }
                                },
                            });
                        }
                    },
                });
            } else {
                result = updateEmail({
                    variables: {
                        id: email.id,
                        emailAddress: data.emailAddress,
                        emailType: data.emailAddressType,
                        principal: data.primary,
                    },
                    optimisticResponse: {
                        editarEmail: {
                            email: {
                                id: email.id,
                                direccion: data.emailAddress,
                                tipoEmail: data.emailAddressType,
                                principal: data.primary,
                                confirmado: email.confirmado,
                                __typename: "Email",
                            },
                            errores: null,
                        }
                    },
                    update(cache, { data: { editarEmail } }) {
                        if (editarEmail?.email?.principal ?? false) {
                            const { perfil } = cache.readQuery({ query: GET_PROFILE });
                            const currentMainEmail = perfil.emails.find(e => e.principal && e.id !== editarEmail.email.id);

                            if (currentMainEmail.id !== editarEmail.email.id) {
                                cache.modify({
                                    id: cache.identify(currentMainEmail),
                                    fields: { principal: () => false },
                                });
                            }
                        }
                    },
                });
            }

            const { data: resultData } = await result;
            const { errores } = isNewEmail ? resultData.agregarEmail : resultData.editarEmail;

            if (errores) {
                setModalError({
                    title: t(`errors.${isNewEmail ? "create" : "update"}.default.title`),
                    message: errores[0].mensaje,
                });
            } else {
                addToast((
                    <div>
                        <div>
                            <strong>{t(isNewEmail ? "addConfirm" : "updateConfirm")}</strong>
                        </div>
                        <div>
                            <SpiMasked type="email">{data.emailAddress}</SpiMasked>
                        </div>
                    </div>
                ), {
                    appearance: "success",
                });
                modalRef?.current && modalRef.current.close();
            }
        } catch (error) {
            errorHandler(error, () => {
                setModalError(t(
                  `errors.${isNewEmail ? "create" : "update"}.default`,
                  { returnObjects: true }
                ));
            });
        }
    };

    return (
        <Modal
            {...attributes}
            title={isNewEmail ? t("addEmail") : t("editEmail")}
            onClose={onCloseHandler}
            ref={modalRef}
        >
            <Form onSubmit={handleSubmit(onSubmit)}>
                <ModalBody>
                    {modalError && (
                        <Row>
                            <Column sm="12">
                                <Alert variant="danger" {...modalError}/>
                            </Column>
                        </Row>
                    )}

                    <Row>
                        <Column sm="12">
                            <FormInput
                              {...formHook}
                              validationRules={validator.email}
                              name="emailAddress"
                              label={t("emailAddress.label")}
                              placeholder={t("emailAddress.placeholder")}
                              defaultValue={isNewEmail ? "" : email.direccion}
                              spiMask="email"
                            />
                        </Column>
                    </Row>
                    <Row>
                        <Column sm="12">
                            <FormSelect
                              {...formHook}
                              name="emailAddressType"
                              label={t("emailAddressType.label")}
                              options={emailAddressTypes}
                              defaultValue={isNewEmail ? emailAddressTypes[0].value : email.tipoEmail}
                            />
                        </Column>
                    </Row>
                    {!isNewEmail && email.confirmado && (
                        <Row>
                            <Column sm="12">
                                <FormCheckbox
                                  {...formHook}
                                  name="primary"
                                  label={t("makePrimary")}
                                  inline
                                  defaultChecked={email.principal}
                                  disabled={email.principal}
                                />
                            </Column>
                        </Row>
                    )}
                </ModalBody>
                <ModalFooter>
                    {/*<Button type="button" block size="md" colors="light" onClick={() => toggleModal(false)}>{t("close")}</Button>*/}
                    <Button type="submit" block size="lg" colors="primaryWithBg" disabled={isSubmitting || loading} loading={isSubmitting}>{t("save")}</Button>
                </ModalFooter>
            </Form>

        </Modal>
    );
};

export default EmailModal;
